// Un comprobante del pedido para verlo y descargarlo (decisión D57). Es la misma hoja A4 que imprime la distribuidora
// (`@reparto/impresion/comprobante-a4`, que se carga recién acá); "Descargar PDF" abre el diálogo de impresión del
// celular o la computadora, donde se elige "Guardar como PDF". Al imprimir, solo sale el comprobante. La factura
// electrónica (D58) es el KuDE, con el QR de la DNIT, y además se descarga su XML.
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { TIPO_DOCUMENTO_FISCAL_ETIQUETA } from '../compartido';
import { Aviso, Boton, ErrorCarga } from '../componentes/base';
import { Pantalla } from '../componentes/Marco';
import { comprobantesDelPedido, guardarXml, nombreDocumento, xmlDelComprobante } from '../datos/comprobantes';
import { mensajeDeError } from '../datos/api';
import { useConsulta } from '../datos/consultas';

type Plantilla = typeof import('@reparto/impresion/comprobante-a4');

export function Comprobante() {
  const { id = '', comprobante = '' } = useParams();
  const c = useConsulta(`comprobantes:${id}`, () => comprobantesDelPedido(id));
  const [plantilla, setPlantilla] = useState<Plantilla | null>(null);
  const [bajando, setBajando] = useState(false);
  const [errorXml, setErrorXml] = useState<string | null>(null);
  useEffect(() => {
    let vivo = true;
    void import('@reparto/impresion/comprobante-a4').then((m) => vivo && setPlantilla(m));
    return () => {
      vivo = false;
    };
  }, []);
  const d = c.datos?.datos.find((x) => x.id === comprobante);
  const titulo = d ? nombreDocumento(d, TIPO_DOCUMENTO_FISCAL_ETIQUETA[d.tipo]) : 'Comprobante';
  useEffect(() => {
    if (!d) return;
    const antes = document.title;
    // El nombre que propone el navegador para el PDF.
    document.title = titulo;
    return () => {
      document.title = antes;
    };
  }, [d, titulo]);

  return (
    <Pantalla titulo={titulo} volver={`/pedidos/${id}`} amplia>
      <style>{`@media print {
  body * { visibility: hidden !important; }
  #comprobante-impreso, #comprobante-impreso * { visibility: visible !important; }
  #comprobante-impreso { position: absolute; inset: 0; }
  @page { size: A4; margin: 10mm; }
}`}</style>
      {c.cargando ? (
        <p className="px-4 py-8 text-center text-stone-600">Buscando el comprobante…</p>
      ) : !c.datos ? (
        <ErrorCarga mensaje={c.error} sinRed={c.sinRed} onReintentar={() => void c.recargar()} />
      ) : !d ? (
        <div className="px-4 py-6">
          <Aviso tono="info" titulo="No encontramos ese comprobante">
            Volvé al pedido para ver los que tiene.
          </Aviso>
        </div>
      ) : (
        <div className="flex flex-col gap-4 px-4 py-4">
          {d.estado !== 'emitido' ? (
            <Aviso tono="info" titulo="Todavía no tiene número">
              La distribuidora lo está emitiendo. Cuando salga, lo vas a ver acá con su número.
            </Aviso>
          ) : null}
          <div className="flex flex-wrap justify-end gap-2">
            {d.sifen && d.estado === 'emitido' && d.cdc ? (
              <Boton
                icono="code"
                variante="secundario"
                disabled={bajando}
                onClick={async () => {
                  setErrorXml(null);
                  setBajando(true);
                  try {
                    guardarXml(await xmlDelComprobante(id, d.id), d.cdc!);
                  } catch (e) {
                    setErrorXml(mensajeDeError(e));
                  } finally {
                    setBajando(false);
                  }
                }}
              >
                Descargar XML
              </Boton>
            ) : null}
            <Boton icono="download" onClick={() => window.print()} disabled={!plantilla}>
              Descargar PDF
            </Boton>
          </div>
          {errorXml ? (
            <Aviso tono="peligro" titulo="No se pudo bajar el XML">
              {errorXml}
            </Aviso>
          ) : null}
          {plantilla ? (
            <>
              <style>{plantilla.ESTILOS_COMPROBANTE_A4}</style>
              <div
                id="comprobante-impreso"
                className="overflow-x-auto rounded-3xl border border-stone-200 bg-white p-2 shadow-sm"
                // La plantilla escapa todo lo que viene del cliente y de la distribuidora.
                dangerouslySetInnerHTML={{ __html: plantilla.fragmentoComprobanteA4(d) }}
              />
            </>
          ) : (
            <p className="py-8 text-center text-stone-600">Armando el comprobante…</p>
          )}
        </div>
      )}
    </Pantalla>
  );
}
